// Back view of the anatomical body map (MUSCLE_BODY_MAP_PLAN.md, Task 4.3).
// React-free / JSX-free (hazard #7).
//
// The skeleton spec lives in the header comment of regionsFront.ts and applies
// verbatim here (hazard #6). The silhouette and head are imported from that
// module rather than redrawn, so the two views register pixel-for-pixel when
// the user toggles between them -- the only silhouette difference is the foot
// detail (heel pads here, toes there), which is what tells a glance which way
// the figure is facing.
//
// As in the front view only the screen-left half of each paired muscle is
// hand-drawn; pairRegions mirrors it to produce the "-r" twin.
//
// ---------------------------------------------------------------------------
// HOW A BACK IS ACTUALLY LAYERED
// ---------------------------------------------------------------------------
// Two muscles cover nearly the whole back and everything else fits around them:
//
//   TRAPEZIUS  a diamond from the OCCIPITAL BONE -- it runs up the back of the
//              neck to the skull, not from the shoulder line -- out to both
//              acromions and down the spine to T12. Split into the upper,
//              middle and lower bands the training taxonomy distinguishes,
//              with the rhomboids taking the medial band they lie under.
//   LATISSIMUS the widest muscle in the body: a triangle whose base runs from
//              roughly T7 down the spine, across the thoracolumbar fascia to
//              the iliac crest, and whose apex converges into the armpit. Its
//              upper-medial corner is genuinely COVERED by the trapezius, so
//              its drawn medial border is the trapezius' lateral edge -- which
//              is exactly what you see on a real back.
//
// Around them: the rotator cuff over the blade of the scapula, teres major as
// the oblique band closing the back of the armpit, and the erector spinae
// columns emerging below the trapezius at T12 and running to the sacrum.
//
// The triceps are drawn as the horseshoe they are -- broad where the long and
// lateral heads separate high on the arm, converging on the olecranon -- which
// is why both regions narrow toward the elbow instead of running parallel.
//
// ---------------------------------------------------------------------------
// THE DEEP LAYER (EXERCISE_LIBRARY_AND_MUSCLE_TAXONOMY_PLAN.md Task 2.3)
// ---------------------------------------------------------------------------
// Six muscles arrived with the taxonomy expansion and every one of them lies
// UNDER something already drawn here. Because this file tiles rather than
// floats, they were given real estate by re-cutting their own covering muscle
// instead of being stacked on top of it -- stacking makes one of the two
// unclickable, which is the same reasoning the front view's transverse
// abdominis note records. The re-cuts, each commented in place:
//
//   trapezius (upper)  its cervical strap narrows from x 95 to x 105 at the
//                      neck, opening the posterior triangle that splenius
//                      capitis and levator scapulae actually occupy. The band
//                      still originates on the occiput -- that is the point the
//                      header above makes and it is preserved.
//   rhomboids,         medial edge moves x 109 -> x 104 from y 160 to y 260,
//   traps (lower),     freeing the paraspinal column for the multifidus. The
//   erector spinae     vertebral furrow between the halves is therefore filled
//                      by the multifidus over that stretch rather than left
//                      as bare silhouette.
//   gluteus medius     gives up its lateral crescent to the gluteus minimus,
//                      which is genuinely the more anterior/lateral of the two.
//   semitendinosus     splits down its length, the medial half becoming the
//                      semimembranosus. The two really do run side by side.
//
// The quadratus lumborum is the one exception: it is drawn OVER the latissimus
// in the lumbar window, exactly as the erector spinae column already is. This
// view treats the lumbar region as a peel-back of the thoracolumbar fascia,
// which is the only way a back view can show anything below it at all.

import type { BodyRegion } from "./geometry.js";
import { VIEWBOX, mirrorPath, pairRegions } from "./geometry.js";
import { BODY_SILHOUETTE, HEAD_OUTLINE } from "./regionsFront.js";

export const BACK_VIEWBOX = VIEWBOX;

/** Heel pad -- the posterior counterpart of the front view's toes. */
const HEEL_LEFT =
  "M 84 484 C 91 484 96 491 95 498 C 94 504 89 507 84 507 C 78 507 73 504 72 498 C 71 491 77 484 84 484 Z";

export const BACK_OUTLINE: string[] = [
  BODY_SILHOUETTE,
  HEAD_OUTLINE,
  HEEL_LEFT,
  mirrorPath(HEEL_LEFT),
];

export const BACK_REGIONS: BodyRegion[] = [
  // --- neck: the two straps the trapezius lies over --------------------------
  // Splenius capitis: the strap leaving the occiput just off the midline and
  // running down and out toward the neck base. Listed before the trapezius so
  // the trapezius paints over the seam the two share.
  ...pairRegions(
    "splenius-capitis",
    "M 107 76 C 103 80 100 85 98 91 C 96 96 95 100 95 104 C 98 104 101 99 103 93 C 105 87 106 80 107 76 Z",
  ),
  // Levator scapulae: lateral to the splenius, running from the upper cervical
  // spine down to the superior angle of the scapula. Its last few units really
  // do disappear under the trapezius, so they are drawn and then painted over
  // rather than stopped short.
  ...pairRegions(
    "levator-scapulae",
    "M 96 87 C 93 92 91 98 89 104 C 88 108 87 111 88 113 C 91 113 93 108 94 103 C 95 97 96 92 96 87 Z",
  ),

  // --- trapezius: three stacked bands, occiput down to T12 -------------------
  // Upper: from the base of the skull, down the back of the neck, out to the
  // acromion. The cervical strap hugs the midline (x 105-109) rather than
  // spreading to x 95 as it first did -- the posterior triangle it vacated is
  // where the splenius and levator above actually sit. Below the neck the band
  // opens out to the acromion exactly as before.
  ...pairRegions(
    "traps-upper",
    "M 109 75 C 107 79 106 84 105 90 C 101 98 96 104 91 108 C 79 106 67 104 56 101 C 62 111 74 119 88 123 C 97 125 104 125 109 125 Z",
  ),
  // Middle: the horizontal fibers, spinous processes out over the scapular spine.
  ...pairRegions(
    "traps-mid",
    "M 109 128 C 98 129 86 133 78 141 C 85 151 96 157 109 158 Z",
  ),
  // Rhomboids: between the scapulae, in the medial band the trapezius overlies.
  // Medial edge held at x 104 (not 109) from here down -- see the multifidus.
  ...pairRegions(
    "rhomboids",
    "M 104 161 C 98 163 92 166 87 173 C 93 180 98 183 104 185 Z",
  ),
  // Lower: the descending triangle back down to the spine at ~T12.
  ...pairRegions(
    "traps-lower",
    "M 104 188 C 99 189 93 194 90 201 C 95 211 100 217 103 223 C 104 214 104 200 104 188 Z",
  ),
  // Multifidus: deep. The short segment-to-segment muscles packed into the
  // groove either side of the spinous processes -- so it is drawn as exactly
  // that groove, the x 104-109 column the three bands above and the erector
  // spinae below were narrowed to expose. Listed before the erector spinae
  // because the erector genuinely lies over it.
  ...pairRegions(
    "multifidus",
    "M 109 160 C 106 163 104 172 104 184 C 104 204 104 224 105 238 C 106 243 107 246 109 247 C 109 218 109 189 109 160 Z",
  ),

  // --- shoulder --------------------------------------------------------------
  // Same deltoid cap as the front view -- broad on the acromion and scapular
  // spine, converging on the deltoid tuberosity at half the humerus.
  ...pairRegions(
    "delt-lateral",
    "M 57 101 C 46 105 37 113 36 125 C 35 137 35 147 37 154 C 41 158 46 158 48 153 C 50 136 54 116 57 101 Z",
  ),
  ...pairRegions(
    "delt-posterior",
    "M 58 101 C 64 104 68 112 68 124 C 68 136 65 147 59 154 C 55 157 50 157 48 153 C 50 136 54 116 58 101 Z",
  ),
  // Rotator cuff: the triangle over the blade of the scapula, tucked under the
  // deltoid and lateral to the trapezius -- where infraspinatus and teres minor
  // actually lie, and the only part of the cuff a surface view can show.
  ...pairRegions(
    "rotator-cuff",
    "M 87 130 C 77 130 70 136 68 146 C 70 158 78 166 88 165 C 91 154 91 140 87 130 Z",
  ),
  // Teres major: the oblique band from the scapula's inferior angle out to the
  // armpit, forming its back wall.
  ...pairRegions(
    "teres-major",
    "M 87 168 C 78 167 72 171 70 178 C 73 186 80 190 88 188 C 91 182 91 173 87 168 Z",
  ),

  // --- back ------------------------------------------------------------------
  // Latissimus dorsi: apex in the armpit, widening down the flank and sweeping
  // in to the lumbar fascia. Its lateral border follows the silhouette through
  // the waist, which is what makes the V-taper read from behind.
  ...pairRegions(
    "lat-dorsi",
    "M 90 168 C 79 167 72 173 71 184 C 72 197 74 210 77 222 C 83 234 95 242 106 241 C 107 232 105 220 101 209 C 97 194 93 178 90 168 Z",
  ),
  // Quadratus lumborum: deep, and the one region here drawn over the latissimus
  // rather than beside it. It is the block between the 12th rib and the iliac
  // crest, lateral to the erector column and clear of it -- the muscle a
  // suitcase carry loads and the one that hikes the hip. Listed after the
  // latissimus for the same reason the erector spinae is: below the lumbar
  // fascia nothing is visible at all unless the fascia is treated as peeled.
  ...pairRegions(
    "quadratus-lumborum",
    "M 91 208 C 87 209 84 211 83 215 C 83 224 84 233 86 240 C 89 242 91 241 92 238 C 92 228 92 218 91 208 Z",
  ),
  // Erector spinae: the two columns emerging from under the trapezius at T12
  // and running down to the sacrum. Medial edge at x 104 rather than x 109 --
  // the groove it gave up is the multifidus above, which is exactly where the
  // multifidus lies relative to it.
  ...pairRegions(
    "erector-spinae",
    "M 104 224 C 100 228 97 235 96 244 C 97 251 100 256 104 260 C 105 250 105 236 104 224 Z",
  ),

  // --- arms ------------------------------------------------------------------
  // Long head medial, lateral head outside; both narrow toward the common
  // tendon at the elbow, which is what makes a triceps read as a horseshoe.
  ...pairRegions(
    "triceps-long",
    "M 66 148 C 58 155 52 168 51 183 C 51 191 54 198 58 200 C 62 196 64 181 65 166 C 66 155 66 149 66 148 Z",
  ),
  ...pairRegions(
    "triceps-lateral",
    "M 48 154 C 42 162 39 174 39 186 C 39 193 41 198 44 198 C 47 190 49 176 49 165 C 49 158 49 154 48 154 Z",
  ),
  ...pairRegions(
    "forearm-extensors",
    "M 58 200 C 49 206 40 218 37 233 C 35 245 36 254 39 259 C 46 259 51 249 54 234 C 57 220 58 206 58 200 Z",
  ),

  // --- hips ------------------------------------------------------------------
  // Gluteus medius is the fan on the upper-outer hip between the iliac crest
  // and the greater trochanter; maximus is the big mass below and medial to it,
  // ending on the gluteal fold at y 302.
  // Minimus first: it is the lateral crescent of what used to be one medius
  // region, which is where the minimus actually sits -- deeper, and further
  // forward around the side of the hip.
  ...pairRegions(
    "glute-minimus",
    "M 73 241 C 69 245 66 249 64 254 C 66 266 74 273 85 271 C 81 268 76 261 74 253 C 73 248 73 244 73 241 Z",
  ),
  ...pairRegions(
    "glute-medius",
    "M 88 238 C 82 238 77 239 73 241 C 73 244 73 248 74 253 C 76 261 81 268 85 271 C 89 260 90 248 88 238 Z",
  ),
  ...pairRegions(
    "glute-maximus",
    "M 109 248 C 92 246 75 253 68 267 C 64 281 70 296 81 303 C 93 308 104 304 109 295 C 110 279 110 262 109 248 Z",
  ),

  // --- hamstrings: the two bellies diverge to frame the popliteal diamond -----
  ...pairRegions(
    "biceps-femoris",
    "M 86 304 C 75 307 68 320 66 338 C 65 351 68 361 73 365 C 78 362 82 349 84 333 C 86 318 87 308 86 304 Z",
  ),
  // The inner hamstring was one region and is now two, split down its own
  // length: the semitendinosus keeps the lateral half, the semimembranosus
  // takes the medial half against the midline. That is how the pair actually
  // lies -- side by side, the semitendinosus' cord of a tendon superficial to
  // the semimembranosus' broader, flatter belly. The medial border is the
  // thigh's own silhouette, which closes in from x 104 at the gluteal fold to
  // x 100 at the knee, so the medial column narrows the whole way down.
  ...pairRegions(
    "semitendinosus",
    "M 101 303 C 95 305 89 318 87 336 C 86 349 90 359 95 364 C 94 355 93 348 93 340 C 94 327 97 314 101 303 Z",
  ),
  ...pairRegions(
    "semimembranosus",
    "M 103 302 C 102 302 102 303 101 303 C 97 314 94 327 93 340 C 93 348 94 355 95 364 C 98 360 100 347 100 332 C 102 315 103 306 103 302 Z",
  ),

  // --- calves ----------------------------------------------------------------
  // Gastrocnemius: two heads as separate subpaths in one region so the diamond
  // of the upper calf reads. The medial head is the larger and hangs lower --
  // the asymmetry that stops a calf looking like a symmetric bulb.
  ...pairRegions(
    "gastrocnemius",
    "M 84 372 C 74 376 67 390 65 407 C 64 421 68 434 74 440 C 80 436 84 421 85 405 C 85 389 85 375 84 372 Z " +
      "M 87 372 C 96 376 102 391 103 409 C 103 427 99 442 92 449 C 88 444 86 426 86 408 C 86 390 86 375 87 372 Z",
  ),
  // Soleus: the flatter mass underneath, emerging on both sides of the
  // gastrocnemius and running down into the achilles.
  ...pairRegions(
    "soleus",
    "M 76 438 C 73 448 72 460 74 472 C 80 479 90 479 95 472 C 96 460 95 448 91 438 C 87 444 80 444 76 438 Z",
  ),
];
